import {ActionType, ConditionType, DateConditionType, StringValidation, WorkflowType} from '../types/enums';
import {
  Action,
  ChangePropertyAction,
  Condition,
  NotificationAction,
  Rule,
  ScriptAction,
  StartWorkflowAction,
} from '../types/types';
import { validateDouble } from "../../global/Components/Form/lib/validation";

const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const numberRegex = /^-?\d+$/;
const doubleRegex = /^-?\d+(\.\d+)?$/;

export const isInt = (value: string) => numberRegex.test(value);

export const isFloat = (value: string) => doubleRegex.test(value);

export const validateEmails = (emails: string) => {
  if (!emails) return true;
  let error = false;
  const emailArr = emails.split(',');
  for (let i = 0; i < emailArr.length; i++) {
    if (!emailRegex.test(String(emailArr[i].trim()).toLowerCase())) {
      error = true;
    }
  }
  return error;
};

export const validateBasicSettings = (rule: Rule, rules: Rule[]) =>
  !rule.name ||
  rule.name === window.Alfresco.messages.global['rpa2.label.new-rule'] ||
  rules.some(item => item.id !== rule.id && item.name === rule.name) ||
  rule.ruleType.length === 0;

//Conditions
export const validateCondition = (item: Condition) => {
  if (!item.property.value || !item.condition.value) {
    return true;
  }

  if (item.condition.value === ConditionType.IS_EMPTY || item.condition.value === ConditionType.IS_NOT_EMPTY) {
    return false;
  }
  
  return !item.value.value ||
    ((item.property.type === 'long' || item.property.type === 'int') && (!isInt(item.value.value) || (Number(item.value.value) >= Number.MAX_SAFE_INTEGER) || (Number(item.value.value) <= Number.MIN_SAFE_INTEGER))) ||
    (item.property.type === 'double' && (!isFloat(item.value.value) || validateDouble(item.value.value))) ||
    ((item.property.type === 'date' || item.property.type === 'datetime') && !item.dateType?.value) ||
    (item.dateType && item.dateType.value === DateConditionType.RELATIVE && !(item.relativeDateUnit?.value || isInt(item.value.value)));
};

export const validateConditions = (rule: Rule) =>{
  return rule.conditions.length > 0 && rule.conditions.some(item => validateCondition(item))
}

export const actionConfig = (item: ScriptAction) => {
  return item.script.hasConfig ? !(item?.script?.scriptErrors?.errors && item?.script?.scriptErrors?.errors.length === 0) : false;
}


//Actions
const validateScriptAction = (item: ScriptAction) => !item.script.value || actionConfig(item);

const validateNotificationAction = (item: NotificationAction) => 
!item.subject ||
  !item.content ||
  !(item.to.authorities.length !== 0 || !validateEmails(item.to.emails) || item.to.properties.length !== 0 ||
    item.cc.authorities.length !== 0 || !validateEmails(item.cc.emails) || item.cc.properties.length !== 0 ||
    item.bcc.authorities.length !== 0 || !validateEmails(item.bcc.emails) || item.bcc.properties.length !== 0
  ) ||
  (item.attachment.attach && item.attachment.files.length === 0)

const validateStartWorkflowAction = (item: StartWorkflowAction) =>
  !item.workflow.name ||
  !item.workflow.subject ||
  !item.workflow.type ||
  (item.workflow.responsible.authorities.length === 0 && item.workflow.responsible.properties.length === 0) ||
  item.term.dueDate === '' ||
  item.term.notificationCount === '' ||
  item.term.notificationInterval === '' ||
  (item.escalate.commit && item.escalate.to.authorities.length === 0 && item.escalate.to.properties.length === 0) ||
  (item.notifications.taskEnd &&
    item.notifications.taskEndReceivers.authorities.length === 0 &&
    item.notifications.taskEndReceivers.properties.length === 0) ||
  (item.notifications.workflowEnd &&
    item.notifications.workflowEndReceivers.authorities.length === 0 &&
    item.notifications.workflowEndReceivers.properties.length === 0) ||
  item.outcomes.some(
    outcome =>
      !outcome.name ||
      outcome.properties.some(
        property =>
          ((property.property.type === 'long' || property.property.type === 'int') &&
            !isInt(property.value[0].value)) ||
          (property.property.type === 'double' && !isFloat(property.value[0].value)) ||
          !property.property.type
      )
  ) ||
  item.workflow.type === WorkflowType.PARALLEL && item.outcomes.every(
      outcome => !outcome.positive
  );

const validateChangePropertyAction = (item: ChangePropertyAction) =>
  (item.availability.available &&
    item.availability.to.authorities.length === 0 &&
    item.availability.to.properties.length === 0) ||
  item.outcomes.some(
    outcome =>
      ((outcome.property.type === 'long' || outcome.property.type === 'int') && !isInt(outcome?.value[0]?.value)) ||
      (outcome.property.type === 'double' && !isFloat(outcome.value[0].value)) ||
      !outcome.property.type
  );

export const validateAction = (item: Action) => {
  const { type, name } = item;
  return (
    !type ||
    //@ts-ignore
    type === ActionType.DEFAULT ||
    !name ||
    name === window.Alfresco.messages.global['rpa2.label.new-action'] ||
    (type === ActionType.EXECUTE_SCRIPT && validateScriptAction(<ScriptAction>item)) ||
    (type === ActionType.SEND_NOTIFICATION && validateNotificationAction(<NotificationAction>item)) ||
    (type === ActionType.START_WORKFLOW && validateStartWorkflowAction(<StartWorkflowAction>item)) ||
    (type === ActionType.CHANGE_PROPERTY_VALUE && validateChangePropertyAction(<ChangePropertyAction>item))
  );
};

export const validateActions = (rule: Rule) => {
  const failedActions: (string | number)[] = [];
  rule.actions.forEach((item, index) => {
    if (validateAction(item)) {
      failedActions.push(index);
    }
  });
  return failedActions;
};

export default (rule: Rule, rules: Rule[]) => {
  let failedValidation = [];

  if (validateBasicSettings(rule, rules)) {
    failedValidation.push(StringValidation.BASIC_SETTINGS);
  }

  if (validateConditions(rule)) {
    failedValidation.push(StringValidation.INITIAL_CONDITIONS);
  }

  failedValidation = [...failedValidation, ...validateActions(rule)];

  return failedValidation;
};
